import { Link } from "@inertiajs/react"
import type { ReactNode } from "react"

import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { SEO } from "@/components/seo"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"

const faqs: { question: string; answer: ReactNode }[] = [
  {
    question: "Is it safe to use StitchPDF?",
    answer:
      "Yes. Most PDF operations run entirely in your browser using your device's resources. Your files are not uploaded to our servers for these operations. For password protection, files are processed server-side with encryption and deleted immediately after processing.",
  },
  {
    question: "Are my files stored on your servers?",
    answer:
      "For most tools (merge, split, reorder, sign, image conversion), files never leave your browser. Password protection requires server-side processing, but files are deleted immediately after. If you're signed in, processed files are stored in your dashboard (24 hours for free accounts, permanently for paid accounts).",
  },
  {
    question: "What happens to my files after processing?",
    answer:
      "Files processed in your browser are never uploaded. For server-processed files, they're deleted immediately after processing. For free accounts, dashboard files are automatically deleted after 24 hours. For paid accounts (Plus and Pro), files remain in your dashboard until you delete them.",
  },
  {
    question: "Do I need to create an account?",
    answer:
      "No. All tools work without an account. Creating an account gives you access to your dashboard and saved signatures. Upgrading to a paid plan gives you permanent file storage.",
  },
  {
    question: "What are the file size and page limits?",
    answer:
      "Free users can process files up to 50MB. There are no page limits per file, but operations are limited to 5 per day on the free plan. Plus and Pro plans offer 50 and 100 daily operations respectively.",
  },
  {
    question: "Which tools process files in my browser?",
    answer: (
      <>
        <Link href="/merge-pdf" className="hover:text-foreground underline">
          Merge
        </Link>
        ,{" "}
        <Link href="/split-pdf" className="hover:text-foreground underline">
          split
        </Link>
        ,{" "}
        <Link href="/reorder-pdf" className="hover:text-foreground underline">
          reorder
        </Link>
        ,{" "}
        <Link href="/sign-pdf" className="hover:text-foreground underline">
          sign
        </Link>
        , and{" "}
        <Link href="/image-to-pdf" className="hover:text-foreground underline">
          image-to-PDF
        </Link>{" "}
        all process entirely in your browser.{" "}
        <Link href="/protect-pdf" className="hover:text-foreground underline">
          Password protection
        </Link>{" "}
        requires server-side processing because encryption libraries need server
        resources.
      </>
    ),
  },
]

const faqTextAnswers: Record<number, string> = {
  6: "Merge, split, reorder, sign, and image-to-PDF all process entirely in your browser. Password protection requires server-side processing because encryption libraries need server resources.",
}

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq, index) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text:
        faqTextAnswers[index] ??
        (typeof faq.answer === "string" ? faq.answer : ""),
    },
  })),
}

export default function Faq() {
  return (
    <>
      <SEO
        title="FAQ | PDF Tools Help & Questions | StitchPDF"
        description="Got questions about StitchPDF? Find answers about file security, browser processing, pricing, and how our free PDF tools work."
        canonical="https://stitchpdf.com/faq"
        ogTitle="FAQ | PDF Tools Help | StitchPDF"
        ogDescription="Common questions about StitchPDF answered."
      >
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </SEO>

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-16">
          <div className="mx-auto max-w-2xl">
            <h1 className="text-4xl font-bold">Frequently Asked Questions</h1>
            <p className="text-muted-foreground mt-4 text-lg">
              Quick answers about StitchPDF
            </p>

            <Accordion type="single" collapsible className="mt-12">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <h2 className="text-base font-normal">
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                  </h2>
                  <AccordionContent>{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  )
}
